import React, { useEffect } from 'react';
import { StyleSheet } from 'react-native';

import { BellIcon } from '@/assets/icons/bell';
import { Loading } from '@/components/Loading';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box, Image, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { BetrAnalytics } from '@/feature/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { usePlayerInsights } from '@/feature/player-profile-sheets/hooks/use-player-insights';
import { League } from '@/types/api.generated';
import { PlayerInsightItemResponse } from '@/types/openapi.generated';

export const PlayerProfileInsights = ({ playerId, league }: { playerId: string; league: League }) => {
    const { data: playerInsights, isLoading } = usePlayerInsights({ playerId });
    const playerInsightItems = playerInsights?.items || [];
    const emptyPlayerInsightsItems = playerInsightItems.length === 0;
    const noInsights = !playerInsights || !playerInsightItems || emptyPlayerInsightsItems;

    useEffect(() => {
        if (isLoading) {
            return;
        }
        if (noInsights) {
            BetrAnalytics.trackEvent(AnalyticsEvent.FANTASY_PLAYER_INSIGHTS_ERROR, {
                league: league,
            });
        } else {
            BetrAnalytics.trackEvent(AnalyticsEvent.FANTASY_PLAYER_INSIGHTS_SHOWN, {
                league: league,
            });
        }
    }, [isLoading, league, noInsights, playerId]);

    if (isLoading) {
        return (
            <Box justifyContent={'center'} flex={1}>
                <Loading />
            </Box>
        );
    }

    if (noInsights) {
        return (
            <Box alignItems={'center'} justifyContent={'center'} flex={1}>
                <Box
                    p={'s16'}
                    backgroundColor={ds.colorToken.background.neutral.subtler}
                    borderRadius={'r64'}
                    marginBottom={'s12'}
                >
                    <BellIcon />
                </Box>
                <Text variant={'bodyMedium'} color={'neutral300'} textAlign={'center'} style={styles.emptyStateText}>
                    We were unable to gather any insights for this player.
                </Text>
            </Box>
        );
    }

    const performanceSummaryInsight = playerInsights?.performanceSummary;
    const onlyPerformanceSummaryAvailable = !!performanceSummaryInsight && playerInsightItems.length === 0;

    return (
        <Box px={'s16'} zIndex={-1}>
            <Box
                p={'s16'}
                backgroundColor={ds.colorToken.background.neutral.subtler}
                borderRadius={'r16'}
                borderWidth={1}
                borderColor={ds.colorToken.border.subtle}
                marginBottom={'s24'}
            >
                <Text variant={'titleMedium'} color={ds.colorToken.text.primary}>
                    Performance summary
                </Text>
                <SizedBox value={2} />
                <Text variant={'bodyMedium'} color={ds.colorToken.text.secondary}>
                    {performanceSummaryInsight}
                </Text>
            </Box>
            {onlyPerformanceSummaryAvailable ? null : (
                <>
                    <Row justifyContent={'space-between'} alignItems={'center'} width={'100%'}>
                        <Box flex={1} height={1} backgroundColor={ds.colorToken.background.neutral.subtler} />
                        <Row
                            p={'s8'}
                            borderColor={ds.colorToken.border.strong}
                            borderRadius={'r10'}
                            borderWidth={1}
                            backgroundColor={ds.colorToken.background.neutral.base}
                            marginHorizontal={'s8'}
                            alignItems={'center'}
                            justifyContent={'center'}
                        >
                            <Text variant={'labelMedium'} lineHeight={12} letterSpacing={0.06} color={'neutral300'}>
                                Powered by {''}
                            </Text>
                            <Image
                                source={require('../../../assets/images/sports_data_io.png')}
                                style={styles.sportsDataImage}
                            />
                        </Row>
                        <Box flex={1} height={1} backgroundColor={ds.colorToken.background.neutral.subtler} />
                    </Row>
                    <Box marginTop={'s20'}>
                        {playerInsightItems.map((insight, index) => (
                            <InsightItem
                                key={`insight-item-${insight.dateGenerated}-${index}`}
                                item={insight}
                                isLastItem={index === playerInsightItems.length - 1}
                            />
                        ))}
                    </Box>
                </>
            )}
        </Box>
    );
};

const InsightItem = ({ item, isLastItem }: { item: PlayerInsightItemResponse; isLastItem?: boolean }) => {
    const { title, news, dateGenerated, opposingTeamName, opposingTeamIconUrl } = item;
    const date = new Date(dateGenerated ?? 0).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
    });

    return (
        <Row alignItems={'flex-start'}>
            <Box alignItems={'center'} height={'100%'}>
                <Image source={{ uri: opposingTeamIconUrl }} style={styles.opposingTeamIcon} />
                {!isLastItem ? (
                    <Box
                        flex={1}
                        width={1}
                        marginVertical={'s4'}
                        backgroundColor={ds.colorToken.background.neutral.subtler}
                    />
                ) : null}
            </Box>
            <Box alignItems={'flex-start'} paddingLeft={'s8'} paddingRight={'s16'} paddingBottom={'s24'} width={'100%'}>
                <Text variant={'bodySmall'} color={'neutral300'}>
                    {date} {opposingTeamName}
                </Text>
                <SizedBox value={4} />
                <Text variant={'buttonLabel'}>{title}</Text>
                <SizedBox value={4} />
                <Text variant={'bodySmall'} color={ds.colorToken.text.secondary}>
                    {news}
                </Text>
            </Box>
        </Row>
    );
};

const styles = StyleSheet.create({
    bullet: {
        margin: 5,
    },
    sportsDataImage: {
        width: 70,
        height: 14,
    },
    emptyStateText: {
        maxWidth: 250,
    },
    opposingTeamIcon: {
        width: 20,
        height: 20,
    },
});
